import { Buffer } from 'buffer';

import createModuleSha256 from './sha256';

export const TIMEOUT = Symbol('Timeout');
export const COMMAND_FAILED = Symbol('Command failed');
export const AUTHENTICATION_FAILED = Symbol('Authentication command failed');
export const SEED_FAILED = Symbol('Seed command failed');
export const UNKNOWN_PIN = Symbol('The PIN code was not found in the internal Sclak table');
export const WRONG_PIN = Symbol('The PIN code was found in the table but is not valid');
export const EXPIRED_PIN = Symbol('The PIN code was found in the table but it is no longer valid');
export const OUT_OF_SYNC = Symbol('The communication with device got out of sync');

/**
 * Check if it's a Sclak peripheral or not
 *
 * NOTE: mind the difference between platforms
 * - on iOS `id` is "8391B4B5-A13B-BDDD-93D5-C4E08FFFE5AE".
 * - on Android `id` is "4C:E2:F1:01:1C:6C" where "4C:E2:F1"
 *   is common for every Sclak device, "01:1C:6C" is the
 *   specific device identifier
 * Don't know the relationship between them, right now the
 * best we can do is to identify the devices by name.
 *
 * @param {{id: string, name: string}}
 * @param {'android' | 'ios' | 'web'} platform
 * @returns {boolean}
 */
export const peripheralIsSclak = ({ id, name }, platform) => {
  if (platform === 'android') {
    // Device's id starts with Sclak's company identifier
    return id.startsWith('4C:E2:F1');
  }
  if (platform === 'ios' || platform === 'web') {
    // Device's name starts with Sclak's company name
    return name.startsWith('Sclak');
  }
  throw new Error(`Platform ${platform} unsupported`);
};

/**
 * Returns di Sclak peripheral id
 *
 * @param {{id: string, name: string}}
 * @param {'android' | 'ios' | 'web'} platform
 * @returns {string}
 */
export const peripheralExtractId = ({ id, name }, platform) => {
  if (platform === 'android') {
    // 4C:E2:F1:01:1C:6C -> 01:1C:6C
    return id.split(':').slice(3).join(':');
  }
  if (platform === 'ios' || platform === 'web') {
    // Sclak 458 011C6C -> 01:1C:6C
    return name
      .split(' ')
      .slice(2)
      .join('')
      .split(/(.{2})/)
      .filter((t) => t !== '')
      .join(':');
  }
  throw new Error(`Platform ${platform} unsupported`);
};

/**
 * Check if the given peripheral has the same id as `searchedId`
 *
 * @param {string} searchedId
 * @returns {function}
 */
export const peripheralCheckId =
  (searchedId) =>
  ({ id, name }, platform) =>
    peripheralExtractId({ id, name }, platform) === searchedId;

// TODO: define type of sha256 function

/**
 * @typedef {Object} State
 * @property {BlePheripheral} peripheral
 * @property {function} sha256
 * @property {Uint8Array} IC
 * @property {Uint8Array} RI
 * @property {Uint8Array} SK
 * @property {Uint8Array} MAC
 */

/**
 * @param {BlePheripheral} peripheral
 * @param {function} sha256 - SHA256 function
 * @param {string} secretCode - Secret code of device
 * @returns {State}
 */
export const initState = (peripheral, sha256, secretCode) => {
  const { IC, RI, SK } = unpackSecret(secretCode);
  const MAC = new Array(32).fill(0);
  return {
    peripheral,
    sha256,
    IC,
    RI,
    SK,
    MAC,
  };
};

/**
 * @param {State} state - Communication state so far
 * @returns {{state: State, req: Uint8Array}}
 */
export const encodeSeedRequest = (state) => {
  return { state, req: [0xa0] };
};

/**
 * @param {State} state - Communication state so far
 * @param {Uint8Array} res - Seed command response received from device
 * @returns {State} - Communication state so far
 */
export const decodeSeedResponse = (state, res) => {
  assert(res[0] === 0x28, SEED_FAILED);
  const buffer = Buffer.from(res);
  // const header = buffer.at(0);
  // const sequenceId = buffer.slice(1, 3);
  const seedFromDevice = buffer.slice(3);
  const OTP = createOTP({
    SD: seed32(seedFromDevice),
    IC: state.IC,
    RI: state.RI,
    SK: state.SK,
  });
  const MAC = state.sha256(OTP, state.MAC, true, true);
  return { ...state, MAC };
};

/**
 * @param {State} state
 * @returns {{state: State, req: Uint8Array}}
 */
export const encodeAuthenticationRequest = (state) => {
  return { state, req: [0xa9, ...state.MAC.slice(0, 16)] };
};

/**
 * @param {State} state - Communication state so far
 * @param {Uint8Array} res - Authentication command response from device
 * @returns {State}
 */
export const decodeAuthenticationResponse = (state, res) => {
  assert(res[0] === 0x29 && res[1] === 0x00, AUTHENTICATION_FAILED);
  return state;
};

/**
 * @param {State} state - Communication state so far
 * @returns {{state: State, req: Uint8Array}}
 */
export const encodeUnlockRequest = (state) => {
  // 0 ID_MSG     0x90
  // 1 R_ID_MSG   0x90
  // 2 CMD_OUT    0xXX
  // 3 MASK_OUT   0xXX
  // 4 DT_ON      0xXX
  // 5 FLAG       0xXX
  // 6-8 PIN_CODE 0xXX

  const command = 0x90; // Unlock
  const action = 0x01; // DEVICE_SET_OUT
  const mask = 0x01; // Digital output to activate on device
  const duration = 10; // Impulse duration in slots of 100ms
  const flag = 0x04; // Use activation time set on device

  // ByteBuffer pinCodeData = SclakPeripheralPin.pinCodeWithString(pin); //
  const pin = Buffer.from('ffffff', 'hex');

  const message = Buffer.alloc(9);
  message.writeUInt8(command, 0);
  message.writeUInt8(command, 1);
  message.writeUInt8(action, 2);
  message.writeUInt8(mask, 3);
  message.writeUInt8(duration, 4);
  message.writeUInt8(flag, 5);
  message.writeUInt8(pin[0], 6);
  message.writeUInt8(pin[1], 7);
  message.writeUInt8(pin[2], 8);

  // TODO: encrypt([...message], state);
  const { encryptedMessage, MAC } = encrypt(
    [...message],
    state.MAC,
    { IC: state.IC, RI: state.RI, SK: state.SK },
    state.sha256,
  );
  return { state: { ...state, MAC }, req: encryptedMessage };
};

/**
 * @param {State} state
 * @param {Uint8Array} res - Unlock command response from device
 * @returns {State} state
 */
export const decodeUnlockResponse = (state, res) => {
  const { message, MAC } = decrypt([...res], state.MAC, { IC: state.IC, RI: state.RI, SK: state.SK }, state.sha256);

  const command = message[0];
  const replica = message[1];
  if (command !== replica) {
    return { ...state, MAC };
    // NOTE: The following should be the recovery procedure to use in this
    // situation but it doesn't work. What happens is that the device unlocks
    // and since we are unable to read the response we retry the whole procedure
    // aka unlocking the device one more time. Therefore for now we always
    // return success.

    // const {MAC: recoveredMAC} = resync(res, state, MAC);
    // return {...state, MAC: recoveredMAC};
  }

  if (message && message.length >= 4) {
    const RES_OP = message[3];
    switch (RES_OP) {
      case 0x00:
        return { ...state, MAC };
      case 0x01:
        throw UNKNOWN_PIN;
      case 0x02:
        throw WRONG_PIN;
      case 0x03:
        throw EXPIRED_PIN;
      default:
        throw COMMAND_FAILED;
    }
  } else {
    throw COMMAND_FAILED;
  }
};

// const resync = (res, state, nextMAC) => {
//   console.log('RESYNC with previous MAC');
//   // TRY TO DECRYPT WITH MAC n-1
//   let aData2 = [...res];
//   const shift = 1;
//   for (let i = shift; i < aData2.length; i++) {
//     // eslint-disable-next-line no-bitwise
//     aData2[i] = aData2[i] ^ state.MAC[i - shift];
//   }

//   let command = aData2[0];
//   let replica = aData2[1];

//   if (command !== replica) {
//     console.log('RESYNC with next MAC');
//     // TRY TO DECRYPT WITH MAC n+1
//     const {message: aData3, MAC: lastMAC} = decrypt(
//       [...res],
//       nextMAC,
//       {IC: state.IC, RI: state.RI, SK: state.SK},
//       state.sha256,
//     );
//     command = aData3[0];
//     replica = aData3[1];

//     if (command !== replica) {
//       console.log('RESYNC failed!!!');
//       throw OUT_OF_SYNC;
//     } else {
//       return {message: aData3, MAC: lastMAC};
//     }
//   } else {
//     return {message: aData2, MAC: state.MAC};
//   }
// };

// ======================================= Low Level Sclak

// TODO: documentation
const encrypt = (message, currentMAC, { IC, RI, SK }, sha256) => {
  // copy current OTP
  // TODO: inline? useless to copy?
  const CMAC = [...currentMAC];

  // SHA256 of OTP and DATA
  const OTPn = createOTP({ SD: CMAC, IC, RI, SK });

  // SHA256 of OTPn
  const MAC = sha256(OTPn, CMAC, true, true);

  // XOR OTPn with MSG (aCommandBytes)
  const encryptedMessage = [...message];
  const shouldCommandReplica = true;
  const shift = shouldCommandReplica ? 1 : 0;
  for (let i = shift; i < message.length; i++) {
    // eslint-disable-next-line no-bitwise
    encryptedMessage[i] = message[i] ^ MAC[i - shift];
  }
  return { encryptedMessage, MAC };
};

// TODO: documentation
const decrypt = (encryptedMessage, currentMAC, { IC, RI, SK }, sha256) => {
  // copy current OTP
  // TODO: inline? useless to copy?
  const CMAC = [...currentMAC];

  // SHA256 of OTP and DATA
  const OTPn = createOTP({ SD: CMAC, IC, RI, SK });

  // SHA256 of OTPn
  const MAC = sha256(OTPn, CMAC, true, true);

  // XOR OTPn with MSG (aCommandBytes)
  let shouldCommandReplica = true;
  let shift = shouldCommandReplica ? 1 : 0;
  let message = [...encryptedMessage];
  for (let i = shift; i < encryptedMessage.length; i++) {
    // eslint-disable-next-line no-bitwise
    message[i] = encryptedMessage[i] ^ MAC[i - shift];
  }
  return { message, MAC };
};

/**
 * Create the OTP (One Time Password) to use in device communication.
 *
 * @param {{SD: Uint8Array, IC: Uint8Array, RI: Uint8Array, SK: Uint8Array}}.
 * @returns {Uint8Array}.
 */
export const createOTP = ({ SD, IC, RI, SK }) => {
  let b = [];
  for (let i = 0; i < 119; i++) {
    b.push(i);
  }
  // FIRST 64 byte page (* 8 64)
  let k = 0;
  let s = 0;
  // Byte 0       IC[3]
  // Byte 1       IC[2]    M0
  // Byte 2       IC[1]
  // Byte 3       IC[0]
  // Byte 4-27    IC[x]    M1-M6
  // Byte 28      IC[31]
  // Byte 29      IC[30]   M7
  // Byte 30      IC[29]
  // Byte 31      IC[28]
  for (let i = 0; i < 8; i++) {
    for (let j = 3; j >= 0; j--) {
      b[k + s] = IC[4 * i + j];
      s++;
    }
  }
  // Byte 32      SD[3]
  // Byte 33      SD[2]    M8
  // Byte 34      SD[1]
  // Byte 35      SD[0]
  // Byte 36-59   SD[x]    M9-M14
  // Byte 60      SD[31]
  // Byte 61      SD[30]   M15
  // Byte 62      SD[29]
  // Byte 63      SD[28]
  k = 32;
  s = 0;
  // TODO: undefined
  for (let i = 0; i < 8; i++) {
    for (let j = 3; j >= 0; j--) {
      b[k + s] = SD[4 * i + j];
      s++;
    }
  }
  // SECOND 64 byte page
  // Byte 64      SK[3]
  // Byte 65      SK[2]    M0
  // Byte 66      SK[1]
  // Byte 67      SK[0]
  // Byte 68-91   SK[x]    M1-M6
  // Byte 92      SK[31]
  // Byte 93      SK[30]   M7
  // Byte 94      SK[29]
  // Byte 95      SK[28]
  k = 64;
  s = 0;
  for (let i = 0; i < 8; i++) {
    for (let j = 3; j >= 0; j--) {
      b[k + s] = SK[4 * i + j];
      s++;
    }
  }
  // RI - M8-M9
  k = 96;
  s = 0;
  for (let i = 0; i < 2; i++) {
    for (let j = 3; j >= 0; j--) {
      b[k + s] = RI[4 * i + j];
      s++;
    }
  }
  // Byte 104 MODE = 0 == MAC CALCULATION, is there others?
  b[104] = 0;
  // Byte 105 PAGE = 0
  b[105] = 0;
  // Byte 106 MAN_ID_H
  b[106] = 0;
  // Byte 107 MAN_ID_L
  b[107] = 0;
  // Byte 108-118 0x0
  for (let i = 108; i <= 118; i++) {
    b[i] = 0x0;
  }
  return b;
};

/**
 * Extract IC (Init Code), RI (ROM ID) and SK (Secret Key) from device's secret
 * code as returned from API (https://api.sclak.com/peripherals).
 *
 * @param {string} secretCode Device's Secret Code hex string.
 * @throws Will throw an exception if secret code is not in the right format.
 * @returns {{IC: Uint8Array, RI: Uint8Array, SK: Uint8Array}} unpacked data.
 */
export const unpackSecret = (secretCode) => {
  const IC = new Array(32).fill(0);
  const RI = new Array(8).fill(0);
  const SK = new Array(32).fill(0);
  if (secretCode.length !== (IC.length + SK.length + RI.length) * 2) {
    throw new Error('Wrong length of secret code');
  }
  let buffer = Buffer.from(secretCode, 'hex');
  for (let i = 0; i < IC.length; i++) {
    IC[i] = buffer[i];
  }
  for (let i = 0; i < SK.length; i++) {
    SK[i] = buffer[IC.length + i];
  }
  for (let i = 0; i < RI.length; i++) {
    RI[i] = buffer[IC.length + SK.length + i];
  }
  return { IC, RI, SK };
};

/**
 * Generate a 32 byte seed from a 16 byte seed received from the device.
 *
 * @param {Uint8Array} seedFromDevice 16 byte seed received from device.
 * @returns {Uint8Array} 32 byte seed (TODO: specify usage).
 */
export const seed32 = (seedFromDevice) => {
  assert(seedFromDevice.length === 16, 'Expected 16 bytes');

  const result = new Array(32).fill(0);
  const seed16Lower = seedFromDevice;
  const seed16Upper = new Array(16).fill(0);

  for (let i = 0; i < 16; i++) {
    // eslint-disable-next-line no-bitwise
    seed16Upper[16 - i - 1] = ~seed16Lower[i] & 0xff;
  }
  for (let i = 0; i < 16; i++) {
    result[i] = seed16Lower[i];
  }
  for (let i = 0; i < 16; i++) {
    result[i + 16] = seed16Upper[i];
  }
  return result;
};

// ======================================= Assertions

/**
 * Throws message when expression is false.
 *
 * @param {boolean} expression - Expression of which we want to assert truthiness.
 * @param {string | symbol} message - Message to throw when expression is false.
 * @returns {void}
 * @throws {Error | symbol}
 */
const assert = (expression, message) => {
  if (!expression) {
    if (typeof message === 'string') {
      throw new Error(message);
    } else if (typeof message === 'symbol') {
      throw message;
    } else {
      throw new Error('assertion error');
    }
  }
};

// ======================================= Timing

/**
 * Returns a promise that will be resolved/rejected as the given promise `p`
 * only if such promise resolves/rejects before given timeout `ms`.
 *
 * If timeout occur then the returned promise will reject with TIMEOUT symbol.
 *
 * @param {Promise<T, E>} p - Promise to give an execution timeout.
 * @param {number} ms - Milliseconds to wait before timeout.
 * @param {function} onTimeout - Called when timeout.
 * @returns {Promise<T, symbol>}
 */
export const withTimeout = async (p, ms, onTimeout) => {
  const [success, result] = await Promise.race([
    Promise.all([Promise.resolve(true), p]),
    Promise.all([Promise.resolve(false), waitFor(ms)]),
  ]);
  if (success) {
    return result;
  }
  onTimeout();
  throw TIMEOUT;
};

/**
 * Returns a promise that waits for given milliseconds to resolve.
 *
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
export const waitFor = (ms) =>
  new Promise((res, _rej) => {
    return setTimeout(res, ms);
  });

// ======================================= SHA256

const initializeEMS = () => {
  return createModuleSha256().then((m) => {
    const computeSha256 = m.cwrap('ComputeSHA256', 'number', ['number', 'number', 'number', 'number', 'number']);

    /**
     * Computes (custom) SHA256 of `message`
     *
     * @param {Uint8Array} message TODO
     * @param {Uint8Array} mac TODO
     * @param {boolean} skipconst TODO
     * @param {boolean} reverse TODO
     * @returns {Uint8Array} hashed `message`
     */
    return (message, mac, skipconst, reverse) => {
      const otp = new Uint8Array(message);
      const otpnDataBytes = otp.length * otp.BYTES_PER_ELEMENT;
      const otpPtr = m._malloc(otpnDataBytes);
      const otpHeap = new Uint8Array(m.HEAPU8.buffer, otpPtr, otpnDataBytes);
      otpHeap.set(new Uint8Array(otp.buffer));

      // Get data byte size, allocate memory on Emscripten heap, and get pointer
      const nDataBytes = mac.length * mac.BYTES_PER_ELEMENT;
      const dataPtr = m._malloc(nDataBytes);

      // Copy data to Emscripten heap (directly accessed from Module.HEAPU8)
      const dataHeap = new Uint8Array(m.HEAPU8.buffer, dataPtr, nDataBytes);
      dataHeap.set(new Uint8Array(mac.buffer));

      // Call function and get result
      computeSha256(otpHeap.byteOffset, otp.length, skipconst ? 1 : 0, reverse ? 1 : 0, dataHeap.byteOffset);
      const result = new Uint8Array(dataHeap.buffer, dataHeap.byteOffset, mac.length);

      // Free memory
      m._free(otpHeap.byteOffset);
      m._free(dataHeap.byteOffset);
      return result;
    };
  });
};

export const createSha256 = () => waitFor(200).then((_) => initializeEMS());
